import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { IdentityConfigurationService, IdentityUserService } from '@auth';
import { environment } from '@environments';
import { NotificationSocketMessage } from '@interfaces';
import { OfficerNotificationsResponse } from './officer-notifications-response';

// BE pushes { data: <pending count> }; boolean / { hasNotifications } kept for compatibility.
export function hasPendingFromSocket(raw: unknown): boolean {
    if (typeof raw === 'boolean') {
        return raw;
    }
    const message = raw as { data?: unknown; hasNotifications?: unknown } | null;
    if (typeof message?.data === 'number') {
        return message.data > 0;
    }
    return !!message?.hasNotifications;
}

@Injectable({
    providedIn: 'root'
})
export class SasNotificationsService {
    private readonly http = inject(HttpClient);
    private readonly identityUserService = inject(IdentityUserService);
    private readonly identityConfigService = inject(IdentityConfigurationService);
    private readonly baseUrl: string = environment.builtEnvironmentApiURL;

    private readonly listUrl = `${this.baseUrl}/api/admin-users/notifications`;
    private readonly readUrl = (id: string) => `${this.baseUrl}/api/admin-users/notifications/${id}/read`;
    private readonly socketPath = '/api/admin-users/notifications/any';

    private socket?: WebSocket;
    private reconnectTimer?: ReturnType<typeof setTimeout>;
    private manualClose = false;

    private readonly messages = new Subject<NotificationSocketMessage>();
    readonly messages$ = this.messages.asObservable();

    // ---- REST ----

    getNotifications(): Observable<OfficerNotificationsResponse> {
        // Small popover data: the full-screen spinner would block the whole app for it.
        return this.http.get<OfficerNotificationsResponse>(this.listUrl, { headers: { 'X-Skip-Spinner': 'true' } });
    }

    readNotification(id: string): Observable<void> {
        return this.http.put<void>(this.readUrl(id), {});
    }

    // ---- WebSocket ----

    connect(): void {
        if (this.socket && this.socket.readyState <= WebSocket.OPEN) {
            return;
        }
        if (!this.identityUserService.isLogged()) {
            return;
        }

        this.manualClose = false;

        const wsBase = this.baseUrl.replace(/^http/i, 'ws');

        // Token removing "Bearer "
        const token = (this.identityUserService.getToken() ?? '')
            .replace(/^Bearer\s+/i, '');

        // Browsers can't set headers on a WebSocket: send the same values as HeaderInterceptor via query params.
        const params = new URLSearchParams({
            access_token: token,
            'x-client':   this.identityConfigService.getClient(),
            'x-service':  this.identityConfigService.getService(),
            'x-product':  this.identityConfigService.getProduct()
        });

        const url = `${wsBase}${this.socketPath}?${params.toString()}`;

        this.socket = new WebSocket(url);

        this.socket.onmessage = (event: MessageEvent) => {
            try {
                this.messages.next({ hasNotifications: hasPendingFromSocket(JSON.parse(event.data)) });
            }
            catch {
                // Ignore non-JSON messages (pings, etc.)
            }
        };

        this.socket.onclose = () => {
            if (!this.manualClose) {
                this.scheduleReconnect();
            }
        };

        this.socket.onerror = () => this.socket?.close();
    }

    disconnect(): void {
        this.manualClose = true;
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = undefined;
        }
        this.socket?.close();
        this.socket = undefined;
    }

    private scheduleReconnect(): void {
        if (this.reconnectTimer) {
            return;
        }
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = undefined;
            this.connect();
        }, 5000);
    }
}
